import React, { useEffect, useState } from 'react';
import { Modal, Button, Table, message, Spin } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { EBillingStatus, TPaymentBilling } from 'types/provider';
import dayjs from 'utils/dayjs';
import PaymentEvent from 'components/ManageSubscription/components/PaymentEventModal/PaymentEvent';
import useInvoiceDetail from 'hooks/useInvoiceDetail';
import s from './s.module.less';
import StatusLabel from 'components/ManageSubscription/components/StatusLabel';
import InvoiceReminderBtn from '../InvoiceReminderBtn';
import ChargeCardModal, { IChargeCardItem } from '../ChargeCardModal';
import CancelInvoiceModal from '../CancelInvoiceModal';
import InvoicePaidSuccessToast from '../InvoicePaidSuccessToast';
import { getPatientPaymentMethodList, providerInvoicePay } from 'api/provider';
import { IKlarityUserPaymentMethodVo } from 'types/invoice';
import { getProviderPrivateFileSigned } from 'api/common';

interface InvoiceDetailModalProps {
    open: boolean;
    onCancel: () => void;
    item?: TPaymentBilling;
    refetchData?: () => void;
    isGroupPractice?: boolean;
}

interface LineItem {
    id: string;
    description: string;
    quantity: number;
    amount: number;
    total: number;
}

const InvoiceDetailModal: React.FC<InvoiceDetailModalProps> = ({
    open,
    onCancel,
    item,
    refetchData,
    isGroupPractice,
}) => {
    // Stays mounted while closed (item also changes for refund / cancel), so only fetch when opened
    const [invoiceDetail, loading, fetchInvoiceDetail] = useInvoiceDetail(item?.invoiceNo || '', {
        manual: true,
        isPracticeView: isGroupPractice,
    });
    const [showChargeCardModal, setShowChargeCardModal] = React.useState(false);
    const [showCancelInvoiceModal, setShowCancelInvoiceModal] = React.useState(false);
    const [charging, setCharging] = React.useState(false);
    const [showInvoicePaidSuccess, setShowInvoicePaidSuccess] = React.useState(false);
    const [patientCards, setPatientCards] = useState<IChargeCardItem[]>([]);
    const [paymentMethodsLoading, setPaymentMethodsLoading] = useState(false);
    const [loadingPdf, setLoadingPdf] = useState(false);

    const invoice = invoiceDetail?.invoice;
    const totalAmount = invoice?.totalAmount?.toFixed(2);

    useEffect(() => {
        if (open && item?.invoiceNo) {
            fetchInvoiceDetail(item.invoiceNo);
        }
    }, [open]);

    const handleSeeDetail = async () => {
        setLoadingPdf(true);
        const response = await getProviderPrivateFileSigned(invoice?.invoicePdfUrl!);
        setLoadingPdf(false);
        if (response && !response.error && response.data?.data?.singedUrl) {
            window.open(response.data.data.singedUrl, '_blank');
        }
    };

    // Patient info - fallback to item data if invoice doesn't have it
    const patientName =
        item?.patientFirstName && item?.patientLastName
            ? `${item.patientFirstName} ${item.patientLastName}`
            : 'Patient';
    const patientEmail = item?.patientEmail;

    // Convert line items from TInvoiceLineItemDetail to LineItem format
    const lineItems: LineItem[] = (invoiceDetail?.lineItems || []).map((item) => ({
        id: item.id.toString(),
        description: item.description || 'Description',
        quantity: item.quantity || 0,
        amount: item.unitPrice || 0,
        total: item.amount || 0,
    }));

    const lineItemsColumns = [
        {
            title: 'DESCRIPTION',
            dataIndex: 'description',
            key: 'description',
            align: 'left' as const,
        },
        {
            title: 'QTY',
            dataIndex: 'quantity',
            key: 'quantity',
            width: 80,
            align: 'center' as const,
        },
        {
            title: 'AMOUNT',
            dataIndex: 'amount',
            key: 'amount',
            width: 120,
            align: 'right' as const,
            render: (amount: number) => `$${amount.toFixed(2)}`,
        },
        {
            title: 'TOTAL',
            dataIndex: 'total',
            key: 'total',
            width: 120,
            align: 'right' as const,
            render: (total: number) => `$${total.toFixed(2)}`,
        },
    ];

    const handleChargeCard = async (paymentMethodId?: string, paymentToken?: string) => {
        const invoiceNo = invoice?.invoiceNo || item?.invoiceNo;
        const clientId = item?.clientId;
        if (!invoiceNo) {
            message.error('Invoice number is missing.');
            return;
        }
        if (clientId == null) {
            message.error('Patient (client) is required to charge.');
            return;
        }
        if (!paymentMethodId && !paymentToken) {
            message.error('Please select a payment method.');
            return;
        }
        try {
            const res = await providerInvoicePay({
                invoiceNo,
                clientId,
                payAmount: Number(totalAmount) || undefined,
                paymentMethodId: paymentMethodId || undefined,
                paymentToken,
                paymentType: 'CREDIT_CARD',
            });
            const data = res?.data?.data;
            if (data?.success) {
                setShowChargeCardModal(false);
                fetchInvoiceDetail(invoiceNo);
                setShowInvoicePaidSuccess(true);
                refetchData?.(); // 扣款成功后刷新父级支付列表
            } else {
                message.error(data?.errorMessage || res?.data?.message || 'Payment failed.');
            }
        } catch (e) {
            message.error((e as Error)?.message || 'Payment failed.');
        }
    };

    /** 将接口返回的支付方式转为 ChargeCardModal 所需结构；新卡置顶、默认卡优先（接口默认顺序可保留） */
    const mapPaymentMethodToCardItem = (pm: IKlarityUserPaymentMethodVo): IChargeCardItem => ({
        id: String(pm.id),
        brand: pm.cardBrand ? pm.cardBrand.charAt(0).toUpperCase() + pm.cardBrand.slice(1).toLowerCase() : 'Card',
        last4: pm.cardLast4 || '',
        expMonth: pm.cardExpMonth || '',
        expYear: pm.cardExpYear || '',
        paymentMethodId: pm.stripePmId || String(pm.id),
    });

    /** 拉取患者支付方式列表（Charge 弹窗打开时或添加新卡后刷新） */
    const fetchPatientPaymentMethods = React.useCallback(() => {
        if (item?.clientId == null) return;
        setPaymentMethodsLoading(true);
        getPatientPaymentMethodList({ clientId: item.clientId })
            .then((res) => {
                if (!res.error && res.data?.data) {
                    const list = (res.data.data as IKlarityUserPaymentMethodVo[]).map(mapPaymentMethodToCardItem);
                    setPatientCards(list);
                } else {
                    setPatientCards([]);
                }
            })
            .catch(() => setPatientCards([]))
            .finally(() => setPaymentMethodsLoading(false));
    }, [item?.clientId]);

    useEffect(() => {
        if (!showChargeCardModal || item?.clientId == null) {
            if (!showChargeCardModal) setPatientCards([]);
            return;
        }
        fetchPatientPaymentMethods();
    }, [showChargeCardModal, item?.clientId, fetchPatientPaymentMethods]);

    return (
        <>
            <Modal
                open={open}
                onCancel={onCancel}
                footer={null}
                width={650}
                centered
                className={s.invoiceDetailModal}
                closeIcon={<CloseOutlined className={s.closeIcon} />}
                destroyOnClose
            >
                <Spin spinning={loading}>
                    <div className={s.modalContent}>
                        {/* Header */}
                        <div className={s.modalHeader}>
                            <div className={s.headerLeft}>
                                <div className={s.headerTop}>
                                    <div className={s.titleSection}>
                                        <h2 className={s.modalTitle}>Invoice {invoice?.invoiceNo}</h2>
                                        <StatusLabel value={invoice?.status} isInvoice item={item} />
                                    </div>
                                    {invoice?.invoicePdfUrl && (
                                        <Button
                                            type="text"
                                            onClick={handleSeeDetail}
                                            className={s.detailsLink}
                                            loading={loadingPdf}
                                        >
                                            See invoice details
                                        </Button>
                                    )}
                                </div>
                                <div className={s.dateInfo}>
                                    {invoice?.createdAt && (
                                        <div className={s.dateItem}>
                                            Created on: {dayjs(invoice?.createdAt).format('MMMM D, YYYY')}
                                        </div>
                                    )}
                                    {invoice?.dueDate && (
                                        <div className={s.dateItem}>
                                            Due on: {dayjs(invoice?.dueDate).format('MMMM D, YYYY')}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Scrollable Content */}
                        <div className={s.scrollableContent}>
                            {/* Billed To & Details */}
                            <div className={s.infoSection}>
                                <div className={s.detailsSection}>
                                    <div className={s.title}>Billed to</div>
                                    <div className={s.patientName}>{patientName}</div>
                                    <div className={s.patientEmail}>{patientEmail}</div>
                                </div>
                                <div className={`${s.detailsSection} ${s.detailsSectionRight}`}>
                                    <div className={s.title}>Details</div>
                                    {invoice?.issueDate && (
                                        <div className={s.detailItem}>
                                            <span className={s.detailLabel}>Issue date:</span>
                                            <span className={s.detailValue}>
                                                {dayjs(invoice?.issueDate).format('MM-DD-YYYY')}
                                            </span>
                                        </div>
                                    )}
                                    <div className={s.detailItem}>
                                        <span className={s.detailLabel}>Amount:</span>
                                        <span className={s.detailValue}>${totalAmount}</span>
                                    </div>
                                </div>
                            </div>

                            {/* Note to Patient */}
                            {invoice?.noteToPatient && (
                                <div className={s.section}>
                                    <div className={s.sectionTitle}>Note to patient</div>
                                    <div className={s.noteContent}>{invoice?.noteToPatient}</div>
                                </div>
                            )}

                            {/* Line Items */}
                            <div className={s.section}>
                                <div className={s.sectionTitle}>Line items</div>
                                <div className={s.tableWrapper}>
                                    <Table
                                        dataSource={lineItems}
                                        columns={lineItemsColumns}
                                        pagination={false}
                                        className={s.lineItemsTable}
                                        rowKey="id"
                                    />
                                    <div className={s.totalRow}>
                                        <span className={s.totalLabel}>Total</span>
                                        <span className={s.totalValue}>${totalAmount}</span>
                                    </div>
                                </div>
                            </div>

                            {/* History & Activity */}
                            <div className={s.section}>
                                <div className={s.sectionTitle}>History & Activity</div>
                                <PaymentEvent
                                    data={invoiceDetail?.events || []}
                                    loading={loading}
                                    isInvoice
                                    item={item}
                                />
                            </div>
                        </div>

                        {/* Footer：仅在 SCHEDULED / SENT / PARTIAL_PAID 状态下展示 */}
                        {[EBillingStatus.SCHEDULED, EBillingStatus.SENT, EBillingStatus.PARTIAL_PAID].includes(
                            invoice?.status as EBillingStatus,
                        ) && (
                                <div className={s.modalFooter}>
                                    <Button
                                        type="primary"
                                        onClick={() => setShowChargeCardModal(true)}
                                        className={s.chargeCardButton}
                                        loading={charging}
                                        disabled={charging}
                                    >
                                        Charge card
                                    </Button>
                                    <div className={s.footerSecondaryButtons}>
                                        {item && <InvoiceReminderBtn item={item} asButton />}
                                        <Button
                                            onClick={() => setShowCancelInvoiceModal(true)}
                                            className={s.secondaryButton}
                                            disabled={
                                                invoice?.status !== EBillingStatus.SENT &&
                                                invoice?.status !== EBillingStatus.SCHEDULED
                                            }
                                        >
                                            Cancel invoice
                                        </Button>
                                    </div>
                                </div>
                            )}
                    </div>
                </Spin>
            </Modal>
            <ChargeCardModal
                open={showChargeCardModal}
                onCancel={() => setShowChargeCardModal(false)}
                onChargeCard={handleChargeCard}
                amount={invoice?.balanceAmount}
                cards={patientCards}
                loading={paymentMethodsLoading}
                clientId={item?.clientId}
                onCardAdded={fetchPatientPaymentMethods}
            />
            <CancelInvoiceModal
                open={showCancelInvoiceModal}
                onCancel={() => {
                    setShowCancelInvoiceModal(false);
                }}
                item={item!}
                onSuccess={() => {
                    setShowCancelInvoiceModal(false);
                    onCancel();
                    refetchData?.();
                }}
            />
            <InvoicePaidSuccessToast
                visible={showInvoicePaidSuccess}
                onClose={() => setShowInvoicePaidSuccess(false)}
            />
        </>
    );
};

export default InvoiceDetailModal;
